import React, { useEffect, useState } from 'react';
import { useOnboardingProgress } from './hooks/useOnboardingProgress';
import { useAppContext } from '../../contexts/AppContext';
import { resolvePostOnboardingPage } from './domain/onboardingExit';
import type { OnboardingStepId } from './domain/onboardingProgress';
import WhatsAppOnboardingStep from './components/WhatsAppOnboardingStep';
import FunnelOnboardingStep from './components/FunnelOnboardingStep';
import OnboardingCompleteStep from './components/OnboardingCompleteStep';
import OnboardingStepper from './components/OnboardingStepper';
import OnboardingHero from './components/OnboardingHero';
import Spinner from '../../components/ui/Spinner';

const OnboardingPage: React.FC = () => {
  const { currentStep, isLoading, isCompleted } = useOnboardingProgress();
  const { setCurrentPage } = useAppContext();
  /** Navegação manual (ex.: voltar da etapa 2) sem desfazer o progresso salvo. */
  const [viewStep, setViewStep] = useState<OnboardingStepId | null>(null);

  const showComplete = isCompleted || currentStep === null;
  const activeStep = showComplete ? null : (viewStep ?? currentStep);
  const goBackToWhatsApp = () => setViewStep(1);
  /** Sai da view manual e volta ao passo do progresso (ex.: funil). */
  const clearManualView = () => setViewStep(null);

  useEffect(() => {
    if (showComplete) setViewStep(null);
  }, [showComplete]);

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-surface-subtle p-s4">
        <Spinner size="lg" />
      </div>
    );
  }

  if (showComplete) {
    return (
      <main className="flex h-full min-h-0 flex-col overflow-y-auto bg-surface-subtle">
        <div className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center p-s4 sm:max-w-2xl sm:p-s6 lg:max-w-3xl lg:p-s8">
          <OnboardingCompleteStep
            onGoToChat={() => setCurrentPage(resolvePostOnboardingPage())}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="flex h-full min-h-0 flex-col overflow-y-auto bg-surface-subtle">
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col p-s4 sm:max-w-2xl sm:p-s6 lg:max-w-3xl lg:p-s8">
        <header className="shrink-0 pb-s4 sm:pb-s5">
          <OnboardingStepper currentStep={activeStep} isComplete={false} />
        </header>

        <div className="flex min-h-0 flex-1 flex-col justify-center">
          {activeStep === 1 && (
            <OnboardingHero title="Conecte o WhatsApp ao CRM">
              <p>
                Integre seu WhatsApp para capturar leads, atender clientes e automatizar seu funil
                de vendas.
              </p>
            </OnboardingHero>
          )}

          {activeStep === 2 && (
            <OnboardingHero title="Configure as etapas do seu funil de vendas">
              <p>Defina as fases pelas quais seus leads passam até fechar negócio.</p>
              <p className="mt-s1">(Você poderá editar depois.)</p>
            </OnboardingHero>
          )}

          <section
            aria-labelledby="onboarding-step-title"
            className="flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-md"
          >
            <div className="min-h-0 flex-1 overflow-y-auto px-s4 py-s5 sm:px-s6 sm:py-s6">
              {activeStep === 1 && (
                <WhatsAppOnboardingStep
                  onAdvance={viewStep === 1 ? clearManualView : undefined}
                />
              )}
              {activeStep === 2 && (
                <FunnelOnboardingStep onBackToPrevious={goBackToWhatsApp} />
              )}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default OnboardingPage;
